/**
 * Configuration slice - handles return center settings
 * Maps to ConfigurationController endpoints
 */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import type { RootState } from 'store';
import axios from 'axios';
import { configService } from 'services/configService';
import { ReturnCenterSettings, ReturnCenterAdditionalCost, ErpSource } from 'returnCenter-api';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import type { ThunkConf } from 'store/thunks';

interface ConfigState {
    settings: ReturnCenterSettings | null;
    settingsLoading: boolean;
    settingsError: string | null;
    settingsRequestId: string | null;
}

const initialState: ConfigState = {
    settings: null,
    settingsLoading: false,
    settingsError: null,
    settingsRequestId: null,
};

// ============ THUNKS ============

export const getReturnCenterSettings = createAsyncThunk<ReturnCenterSettings, void, ThunkConf>(
    'config/getSettings',
    async (_, { rejectWithValue }) => {
        try {
            return await configService.getReturnCenterSettings();
        } catch (error) {
            console.error('Error fetching return center settings:', error);
            if (axios.isCancel(error)) return rejectWithValue(undefined);
            toast.warning(t('settings.loadError', 'Failed to load settings. Some features may be unavailable.'), {
                autoClose: 5000,
                closeOnClick: true,
                toastId: 'settings-error',
            });
            return rejectWithValue({ message: t('settings.loadError', 'Failed to load return center settings') });
        }
    },
);

// ============ SLICE ============

const configSlice = createSlice({
    name: 'config',
    initialState,
    reducers: {
        resetConfigState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getReturnCenterSettings.pending, (state, action) => {
                state.settingsLoading = true;
                state.settingsError = null;
                state.settingsRequestId = action.meta.requestId;
            })
            .addCase(getReturnCenterSettings.fulfilled, (state, action) => {
                if (state.settingsRequestId !== action.meta.requestId) return;

                state.settingsLoading = false;
                state.settings = action.payload;
            })
            .addCase(getReturnCenterSettings.rejected, (state, action) => {
                if (state.settingsRequestId !== action.meta.requestId) return;

                state.settingsLoading = false;
                state.settingsError = (action.payload as any)?.message ?? 'Failed to load settings';
            });

        // Reset on company change
        builder.addCase(companyChanged, () => initialState);
    },
});

// ============ SELECTORS ============

export const selectSettings = (state: RootState) => state.config.settings;

/**
 * Whether this company credits the return slips it receives from batches together, from the batch,
 * or each as it is completed. Together unless the company says otherwise.
 */
export const selectCreditsBatchSlipsTogether = (state: RootState) =>
    state.config.settings?.creditBatchSlipsTogether ?? true;

/**
 * Whether this company credits its customers elsewhere and only sends its returns on in batches:
 * completing a return then creates no credit, and everything about the credit is hidden.
 */
export const selectIsBatchSendingCompany = (state: RootState) => state.config.settings?.batchSendingCompany ?? false;
export const selectSettingsLoading = (state: RootState) => state.config.settingsLoading;
export const selectSettingsError = (state: RootState) => state.config.settingsError;
export const selectReasonCodes = createSelector(selectSettings, (settings) => settings?.reasonCodes);
const selectIsM3Company = createSelector(
    selectSettings,
    (settings) => settings?.erpSource === ErpSource.M3Prod || settings?.erpSource === ErpSource.M3Dev,
);

export const selectReasonCodesMap = createSelector(
    selectReasonCodes,
    (reasonCodes) => reasonCodes?.map((reason) => ({ label: reason.name, value: reason.id })) ?? [],
);

// Reason codes marked as "picked the wrong item" for this context. A row with one of these may get a
// mis-pick line for the item that was actually delivered.
export const selectMisPickReasonCodeIds = createSelector(
    selectReasonCodes,
    (reasonCodes) =>
        new Set<number>(
            (reasonCodes ?? [])
                .filter((reason) => reason.isMisPick)
                .map((reason) => reason.id)
                .filter((id): id is number => id != null),
        ),
);

export const selectAdditionalCostOptions = createSelector(selectSettings, (settings) =>
    (settings?.returnCenterAdditionalCosts ?? [])
        .map((cost: ReturnCenterAdditionalCost) => ({
            label: cost.description,
            value: cost.id,
            data: cost,
            key: cost.id,
        }))
        .filter(Boolean),
);

export const selectHasAdditionalCostOptions = createSelector(
    selectAdditionalCostOptions,
    (options) => options.length > 0,
);

export const selectSelectedAdditionalCostOption = createSelector(
    [
        selectAdditionalCostOptions,
        (_: RootState, selectedCostOption: ReturnCenterAdditionalCost | string | null) => selectedCostOption,
    ],
    (options, selectedCostOption) => {
        if (!selectedCostOption) return null;
        const id = typeof selectedCostOption === 'string' ? selectedCostOption : selectedCostOption.id;
        return options.find((option) => option.data.id === id)?.data ?? null;
    },
);

export const selectSettingsAvailable = createSelector(
    [selectSettings, selectSettingsError, selectSettingsLoading],
    (settings, error, loading) => ({
        isAvailable: settings !== null && !error && !loading,
        isLoading: loading,
        hasError: !!error,
        hasReasonCodes: (settings?.reasonCodes?.length ?? 0) > 0,
        hasAdditionalCosts: (settings?.returnCenterAdditionalCosts?.length ?? 0) > 0,
    }),
);

export const selectCreditLabelOptions = createSelector(selectSettings, (settings) =>
    (settings?.creditLabels ?? [])
        .map((label) => ({
            label: label.name,
            value: label.value,
        }))
        .filter(Boolean),
);

// ============ EXPORTS ============

export const configActions = configSlice.actions;
export const { resetConfigState } = configSlice.actions;

export const configThunks = {
    getReturnCenterSettings,
};

export const configSelectors = {
    selectSettings,
    selectCreditsBatchSlipsTogether,
    selectIsBatchSendingCompany,
    selectSettingsLoading,
    selectSettingsError,
    selectReasonCodes,
    selectReasonCodesMap,
    selectMisPickReasonCodeIds,
    selectAdditionalCostOptions,
    selectHasAdditionalCostOptions,
    selectSelectedAdditionalCostOption,
    selectSettingsAvailable,
    selectCreditLabelOptions,
    selectIsM3Company,
};

export default configSlice.reducer;
